import React, { useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { Theme, useMediaQuery, useScrollTrigger } from "@mui/material";
import * as Sentry from "@sentry/browser";
import { useMandatorContext } from "@bn/theming";
import { USE_INTERNAL_AUTH } from "@helpers";
import {
  ARIVA_HEADER_HEIGHT_DESKTOP,
  ARIVA_HEADER_HEIGHT_MOBILE,
  ARIVA_HEADER_HIDE_SCROLL_THRESHOLD,
  ARIVA_HEADER_ROOT_CLASS,
} from "./arivaHeaderConfig";
import { setArivaHeaderFailed } from "./arivaHeaderStatus";
import { useArivaLogout } from "./useArivaLogout";

/**
 * Container for the injected header markup. The bundle's `<header>` is
 * `position: sticky`, but it can only stick within its parent's box — so the
 * container itself is made sticky to keep the header pinned to the top of the
 * page while the content scrolls underneath. Sits above the sidebar.
 *
 * `min-height` reserves the header's space up front (it mirrors the bundle's
 * `h-[85px] sm:h-[140px]`, Tailwind `sm` = 640px) so the content doesn't jump
 * down when the header markup finishes loading asynchronously.
 *
 * It is shown only near the top of the page and slides out once scrolled past
 * the threshold (`hidden` toggles the transform).
 *
 * When shown the transform is `none` (not `translateY(0)`) and `will-change`
 * is not set: any transform/will-change would make this a containing block for
 * fixed-position descendants, which would trap the bundle's `position: fixed`
 * mobile menu panels inside the header instead of overlaying the viewport.
 */
const HeaderRoot = styled("div", {
  shouldForwardProp: (prop) => prop !== "hidden",
})<{ hidden?: boolean }>`
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.drawer + 2};
  min-height: ${ARIVA_HEADER_HEIGHT_MOBILE}px;
  box-shadow: ${({ theme }) => theme.shadows[2]};
  transform: ${({ hidden }) => (hidden ? "translateY(-100%)" : "none")};
  transition: transform 0.3s ease;

  @media (min-width: 640px) {
    min-height: ${ARIVA_HEADER_HEIGHT_DESKTOP}px;
  }
`;

/**
 * Embeds the standalone Ariva header on top of the app.
 *
 * Rendered only for the `ariva` mandator — for any other mandator it renders
 * nothing and never loads the bundle assets. The header is a self-contained
 * Vue widget; this component only injects its markup into a light-DOM
 * container, loads its assets, and mounts/unmounts it with the React
 * lifecycle.
 */
export const ArivaHeader = () => {
  const { isAriva } = useMandatorContext();
  const containerRef = useRef<HTMLDivElement>(null);
  // The header is desktop-only; mobile uses the app's own responsive nav.
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const enabled = isAriva && !isMobile;
  useArivaLogout(isAriva && !USE_INTERNAL_AUTH);
  // Shown only near the top; hidden once scrolled past the threshold.
  const hidden = useScrollTrigger({
    disableHysteresis: true,
    threshold: ARIVA_HEADER_HIDE_SCROLL_THRESHOLD,
  });

  useEffect(() => {
    if (!enabled) return;
    const container = containerRef.current;
    if (!container) return;

    // Loaded lazily: the loader inlines the (large) bundle assets, so this
    // keeps them out of the main chunk for non-ariva mandators.
    let active = true;
    void import("./arivaHeaderLoader")
      .then(async ({ mountArivaHeader }) => {
        if (!active) return;
        await mountArivaHeader(container);
        setArivaHeaderFailed(false);
      })
      .catch((error) => {
        setArivaHeaderFailed(true);
        Sentry.captureException(error);
      });

    return () => {
      active = false;
      void import("./arivaHeaderLoader").then(({ unmountArivaHeader }) => {
        unmountArivaHeader(container);
      });
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <HeaderRoot
      ref={containerRef}
      className={ARIVA_HEADER_ROOT_CLASS}
      hidden={hidden}
    />
  );
};
